import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";

import { AuthProvider } from "./context/AuthContext";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import { Services, ServiceDetail } from "./pages/Services";
import Book from "./pages/Book";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import Contact from "./pages/Contact";
import { Suburbs, SuburbDetail, LegacySuburbRedirect, SuburbRouteResolver } from "./pages/Suburbs";
import { suburbsData } from "./data/suburbs";
import { Blog, BlogPost } from "./pages/Blog";
import Privacy from "./pages/Privacy";
import Review from "./pages/Review";
import Retired from "./pages/Retired";
import Reviews from "./pages/Reviews";
import NotFound from "./pages/NotFound";
import AdminLogin from "./pages/admin/Login";
import AdminDashboard from "./pages/admin/Dashboard";
import FlyersIndex from "./pages/admin/FlyersIndex";
import FlyerResidents from "./pages/admin/FlyerResidents";
import FlyerSeniors from "./pages/admin/FlyerSeniors";
import { PortalAuthProvider } from "./context/PortalAuthContext";
import PortalLogin from "./pages/portal/Login";
import PortalActivate from "./pages/portal/Activate";
import PortalHome from "./pages/portal/Home";
import PortalJobDetail from "./pages/portal/JobDetail";
import PortalSecurity from "./pages/portal/Security";
import AdelaideEnquiry from "./pages/AdelaideEnquiry";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

function MarketingRoutes() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        {/* Retired business service URLs — 410 treatment */}
        <Route path="/services/managed-it-services-adelaide" element={<Retired />} />
        <Route path="/services/business-voip-phone-systems-adelaide" element={<Retired />} />
        <Route path="/services/pos-network-software-support" element={<Retired />} />
        <Route path="/services/data-cabling" element={<Retired />} />
        <Route path="/services/hardware-firewalls" element={<Retired />} />
        {/* Redirected business service URLs — residential replacements */}
        <Route path="/services/unifi-business-networking-adelaide" element={<Navigate to="/services/unifi-home-networking-adelaide" replace />} />
        <Route path="/services/microsoft-365-business-adelaide" element={<Navigate to="/services/email-solutions" replace />} />
        <Route path="/services/business-cyber-security" element={<Navigate to="/services/anti-scamming-cyber-awareness" replace />} />
        <Route path="/services/:slug" element={<ServiceDetail />} />
        <Route path="/book" element={<Book />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/suburbs" element={<Suburbs />} />
        {/* SEO-canonical, keyword-rich URLs — one explicit route per suburb because
            React Router v7 path params can only match a full segment (so a single
            pattern like '/computer-repairs-:slug' won't bind the slug). 62 routes
            are cheap; this generates the exact pages Google has indexed. */}
        {suburbsData.map((s) => (
          <Route
            key={s.slug}
            path={`/computer-repairs-${s.slug}`}
            element={<SuburbDetail slug={s.slug} />}
          />
        ))}
        {/* Catch-all for unknown 'computer-repairs-<thing>' URLs → 404 via main *
            route below if we don't match it here. We render the resolver so we
            still keep the bot-friendly behaviour of sending bad slugs to /suburbs. */}
        <Route path="/computer-repairs-:legacySlug" element={<SuburbRouteResolver />} />
        {/* Backwards-compatible redirect for any /suburbs/:slug links still floating around the web. */}
        <Route path="/suburbs/:slug" element={<LegacySuburbRedirect />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/review" element={<Review />} />
        <Route path="/business" element={<Retired />} />
        <Route path="/business-support-options" element={<Retired />} />
        <Route path="/reviews" element={<Reviews />} />
        <Route path="/commercial-fit-outs" element={<Retired />} />
        <Route path="/case-studies" element={<Retired />} />
        <Route path="/case-studies/:slug" element={<Retired />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PortalAuthProvider>
            <ScrollToTop />
            <Routes>
              {/* Standalone ad landing page — noindex, not in nav/sitemap, no site chrome */}
              <Route path="/adelaide-enquiry" element={<AdelaideEnquiry />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/flyers" element={<FlyersIndex />} />
              <Route path="/admin/flyers/residents" element={<FlyerResidents />} />
              <Route path="/admin/flyers/seniors" element={<FlyerSeniors />} />
              <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
              <Route path="/portal/login" element={<PortalLogin />} />
              <Route path="/portal/activate/:token" element={<PortalActivate />} />
              <Route path="/portal/security" element={<PortalSecurity />} />
              <Route path="/portal/jobs/:id" element={<PortalJobDetail />} />
              <Route path="/portal" element={<PortalHome />} />
              <Route path="/portal/*" element={<Navigate to="/portal" replace />} />
              <Route path="/*" element={<MarketingRoutes />} />
            </Routes>
          </PortalAuthProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
